import React from 'react';
import { Status } from '@/components';
import { PackageHeldByList, PackageVisibility } from '@/types/package';
import {
  campaignUrl,
  formatHolderLine,
  formatHolderMeta,
  HELD_BY_FREE_HINT,
  HELD_BY_FREE_LABEL,
  HELD_BY_UNKNOWN_LABEL,
  holderStatusLabel,
  pastHolders,
  toHeldByCell,
} from '../packageDisplay';

interface PackageHoldersNoticeProps {
  visibility: PackageVisibility;
  heldBy: PackageHeldByList;
  /** Any makes it a warning, not a block: the price is set at booking. */
  changedFields: string[];
}

/** The usage box inside the Visibilitas card of a saved Khusus package. */
export const PackageHoldersNotice: React.FC<PackageHoldersNoticeProps> = ({
  visibility,
  heldBy,
  changedFields,
}) => {
  const cell = toHeldByCell(heldBy, visibility);
  if (cell.kind === 'none') return null;

  // Unknown warns like held: an unchecked reach is not waved through.
  const isWarning = cell.kind !== 'free' && changedFields.length > 0;
  const past = heldBy ? pastHolders(heldBy) : [];
  // ponytail: one live holder is the rule, so one link; per-row links if that changes.
  const link =
    cell.kind === 'holders' ? campaignUrl(cell.holders[0].campaignId) : null;

  return (
    <div
      role={isWarning ? 'alert' : 'status'}
      className="mt-3 space-y-3 rounded-lg border border-[#DDD6FE] bg-[#F5F3FF] p-3.5 text-[13px] leading-5 text-gray-700"
    >
      <p className="flex items-center gap-2 font-medium text-gray-900">
        <i
          className="ri-megaphone-line text-[16px] text-primary"
          aria-hidden="true"
        />
        {cell.kind === 'holders'
          ? 'Sedang dipakai campaign referral'
          : cell.kind === 'unknown'
            ? `Pemakaian campaign: ${HELD_BY_UNKNOWN_LABEL.toLowerCase()}`
            : HELD_BY_FREE_LABEL}
      </p>

      {cell.kind === 'holders' && (
        <ul className="space-y-2">
          {cell.holders.map((holder) => (
            <li
              key={holder.campaignId}
              className="flex items-start justify-between gap-3 rounded-md bg-white px-3 py-2"
            >
              <div className="min-w-0">
                <p className="font-medium text-gray-900">
                  {holder.campaignName}
                </p>
                <p className="text-[12px] text-gray-500">
                  {formatHolderMeta(holder)}
                </p>
              </div>
              <Status
                value={holderStatusLabel(holder)}
                type="campaignStatus"
                size="small"
              />
            </li>
          ))}
        </ul>
      )}

      <p
        className={isWarning ? 'flex items-start gap-1.5 text-orange-700' : ''}
      >
        {isWarning && (
          <i
            className="ri-alert-line text-[16px] leading-5"
            aria-hidden="true"
          />
        )}
        <span>
          {cell.kind === 'free' && HELD_BY_FREE_HINT}
          {cell.kind === 'holders' &&
            (isWarning
              ? `Perubahan ${changedFields.join(', ')} berlaku untuk booking baru lewat campaign di atas. Referral yang sudah berjalan memakai harga saat booking.`
              : 'Selama dipakai, package tidak bisa diarsipkan atau dipakai campaign lain. Perubahan harga berlaku untuk booking baru; referral yang sudah berjalan memakai harga saat booking.')}
          {cell.kind === 'unknown' &&
            (isWarning
              ? `Perubahan ${changedFields.join(', ')} berlaku untuk booking baru lewat campaign yang memakai package ini, dan campaign itu tidak bisa dicek saat ini.`
              : 'Jika ada campaign yang memakainya, perubahan harga dan skema berlaku untuk booking baru saja.')}
        </span>
      </p>

      {link && (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 font-medium text-primary-600 hover:text-primary-700"
        >
          Buka campaign di On Demand System
          <i className="ri-arrow-right-up-line" aria-hidden="true" />
        </a>
      )}

      {past.length > 0 && (
        <div className="border-t border-[#DDD6FE] pt-3">
          <p className="flex items-center gap-1.5 font-medium text-gray-900">
            <i className="ri-history-line text-[15px]" aria-hidden="true" />
            Riwayat campaign
          </p>
          <ul className="mt-1 space-y-0.5 text-gray-500">
            {past.map((holder) => (
              <li key={holder.campaignId}>{formatHolderLine(holder)}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PackageHoldersNotice;
